import DownloadIcon from "@mui/icons-material/SaveAltRounded";
import { Alert, Box, Button, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import { useCallback, useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import type { JobProgress } from "@entrilia/shared/api/types";
import AsyncJobProgress from "@entrilia/shared/components/AsyncJobProgress";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import useJobProgress from "@entrilia/shared/hooks/useJobProgress";
import { logError } from "@entrilia/shared/logging";
import { downloadFileFromUrl } from "@entrilia/shared/services/downloadFile";
import adminApi from "../../../../api/adminApi";
import ExportDialogIcon from "@entrilia/shared/icons/ExportDialogIcon";

const startExport = withErrorHandling(adminApi.startOrganizationContactsActivityLogExport);

interface Props {
  onClose: () => void;
}

type ExportState =
  | { status: "idle" }
  | { status: "in-progress" }
  | { status: "success"; downloadUrl: string }
  | { status: "error"; message?: string }
  | { status: "polling-failed" };

const ExportOrganizationContactsActivityLogDialog = ({ onClose }: Props) => {
  const [exportState, setExportState] = useState<ExportState>({ status: "idle" });
  const [downloading, setDownloading] = useState(false);

  const handleJobFinished = useCallback((jobProgress: JobProgress) => {
    if (jobProgress.isSuccess) {
      const downloadUrl = jobProgress.properties["DownloadUrl"];
      if (!downloadUrl) {
        setExportState({ status: "error", message: "Download URL was not returned by the server." });
        return;
      }
      setExportState({ status: "success", downloadUrl });
    } else {
      setExportState({ status: "error", message: jobProgress.errorMessage });
    }
  }, []);

  const handleProgressPollingError = useCallback(() => {
    setExportState({ status: "polling-failed" });
  }, []);

  const { jobProgress, startJobProgressCheck } = useJobProgress(
    adminApi.getOrganizationContactsActivityLogExportJobProgress,
    {
      onJobFinished: handleJobFinished,
      onPollingError: handleProgressPollingError,
      updateIntervalMs: 500,
    }
  );

  const handleStartExport = async () => {
    setExportState({ status: "in-progress" });

    const [jobStartResult, jobStartError] = await startExport();

    if (jobStartError) {
      logError(
        jobStartError,
        "[ExportOrganizationContactsActivityLogDialog] startOrganizationContactsActivityLogExport"
      );
      setExportState({ status: "error", message: jobStartError.message });
      return;
    }

    startJobProgressCheck(jobStartResult.operationId);
  };

  const handleDownload = async () => {
    if (exportState.status !== "success") {
      return;
    }
    setDownloading(true);
    await downloadFileFromUrl(exportState.downloadUrl);
    setDownloading(false);
  };

  const isInProgress = exportState.status === "in-progress";

  return (
    <SmallDialog open onClose={isInProgress ? undefined : onClose}>
      <DialogTitle>Export Activity Log</DialogTitle>
      {!isInProgress && <DialogCloseButton onClick={onClose} />}

      <DialogContent>
        {exportState.status === "idle" && (
          <Stack spacing={2} sx={{ alignItems: "center" }}>
            <ExportDialogIcon sx={{ width: 80, height: 80 }} />

            <Stack spacing={1} sx={{ alignItems: "center" }}>
              <Typography noWrap variant="h6">
                Export organization contacts activity log?
              </Typography>

              <Typography color="textSecondary" sx={{ textAlign: "center" }}>
                All changes made to organization contacts will be exported to a CSV file. The export will run in the
                background and may take a few minutes. We&apos;ll also send you an email with a download link.
              </Typography>
            </Stack>
          </Stack>
        )}

        {exportState.status === "in-progress" && (
          <Box sx={{ minHeight: "9rem" }}>
            <AsyncJobProgress title="Preparing your export…" jobProgress={jobProgress} />
          </Box>
        )}

        {exportState.status === "success" && (
          <Alert severity="success">Your export is ready. Click Download to save the file.</Alert>
        )}

        {exportState.status === "polling-failed" && (
          <Alert severity="info">
            We couldn&apos;t check the export&apos;s progress. If it&apos;s still running, you&apos;ll receive an email
            when it&apos;s ready.
          </Alert>
        )}

        {exportState.status === "error" && (
          <Alert severity="error">{exportState.message ?? "Export failed. Please try again."}</Alert>
        )}
      </DialogContent>

      <DialogButtons onCancel={isInProgress ? undefined : onClose} cancelText="Close">
        {exportState.status === "idle" && (
          <Button variant="contained" onClick={handleStartExport}>
            Start Export
          </Button>
        )}

        {exportState.status === "success" && (
          <Button variant="contained" loading={downloading} startIcon={<DownloadIcon />} onClick={handleDownload}>
            Download
          </Button>
        )}
      </DialogButtons>
    </SmallDialog>
  );
};

export default ExportOrganizationContactsActivityLogDialog;
